import React, { useState, useEffect, useCallback } from 'react';
import './RPAWizardModal.css';
import './SPDWizardModal.css';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { nowWallClockInViewerZone } from '../../utils/dateTime';

const TOTAL_STEPS = 10;

const STEPS = [
  { id: 1,  label: 'Property Info' },
  { id: 2,  label: 'Building' },
  { id: 3,  label: 'Roof & Appliances' },
  { id: 4,  label: 'Electrical' },
  { id: 5,  label: 'HVAC & Heat' },
  { id: 6,  label: 'Water & Sewer' },
  { id: 7,  label: 'Flooding & Env' },
  { id: 8,  label: 'Legal & Access' },
  { id: 9,  label: 'HOA & District' },
  { id: 10, label: 'Review & Confirm' },
];

const STEP_ICONS = {
  1:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>,
  2:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>,
  3:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>,
  4:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  5:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 0 1 10 10c0 5.52-4.48 10-10 10S2 17.52 2 12"/><path d="M12 6v6l4 2"/></svg>,
  6:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  7:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  8:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  9:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  10: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
};

const mkYN = () => ({ yn: '', comment: '' });

const initForm = () => ({
  // Step 1
  dateCompleted: '',
  sellerNames: '',
  yearBuilt: '',
  yearAcquired: '',
  occupying: '',
  ifNotOccupying: '',
  // Step 2
  s2: {
    structural: mkYN(), structuralSupports: mkYN(), moisture: mkYN(), termites: mkYN(),
    hailWind: mkYN(), windowLeaks: mkYN(), eifs: mkYN(), subfloor: mkYN(),
  },
  settling: [],
  settlingComment: '',
  // Step 3
  roofAge: '', roofMaterial: '', roofWarranty: '', warrantyExp: '', warrantyTransfer: '',
  s3roof: {
    leak: mkYN(), damage: mkYN(), skylight: mkYN(), gutter: mkYN(),
  },
  s3app: {
    dryer: { yn: '', comment: '', type: '' },
    dishwasher: mkYN(), refrigerator: mkYN(),
    oven: { yn: '', comment: '', fuel: '', config: '' },
    range: { yn: '', comment: '', fuel: '', config: '' },
    microwave: { yn: '', comment: '', type: '' },
    vacuum: mkYN(), trash: mkYN(),
  },
  // Step 4
  electricAmps: '', electricProvider: '', cableProvider: '', internetProvider: '',
  internetType: '', solarPanels: '', securitySystem: '',
  s4now: {
    security: mkYN(), smoke: mkYN(), carbon: mkYN(), fixtures: mkYN(), garage: mkYN(),
  },
  s4ever: {
    service: mkYN(), aluminum: mkYN(), solar: mkYN(), panel: mkYN(),
  },
  // Step 5
  furnaceType: '', furnaceUnits: '',
  fireplaceTypes: [],
  lastChimney: '', fuelTanks: '', acType: '', acUnits: '',
  s5: {
    furnace: mkYN(), centralAC: mkYN(), heatPump: mkYN(), evaporative: mkYN(),
    fireplace: mkYN(), fireplaceInsert: mkYN(), heatingstove: mkYN(),
    fuelTanksP: mkYN(), sumpPump: mkYN(), garageDoors: mkYN(),
  },
  // Step 6
  waterSupplyType: '', wellPermit: '', drillingRecords: '', waterProvider: '',
  waterProviderPhone: '', waterHeaters: '', waterFilter: '', waterSoftener: '',
  s6: {
    leaks: mkYN(), well: mkYN(), heater: mkYN(), sprinkler: mkYN(), potability: mkYN(), sewer: mkYN(),
  },
  sewerType: '', sewerLastScoped: '', septicLastPumped: '',
  // Step 7
  s7flood: {
    flooding: mkYN(), drainage: mkYN(), intrusion: mkYN(), repairs: mkYN(), ponds: mkYN(),
  },
  s7env: {
    hazardous: mkYN(), tanks: mkYN(), expansive: mkYN(), floodplain: mkYN(), oilGas: mkYN(), meth: mkYN(),
  },
  s7radon: {
    tested: mkYN(), detected: mkYN(), mitigation: mkYN(),
  },
  // Step 8
  s8legal: {
    zoning: mkYN(), condemnation: mkYN(), buildingCode: mkYN(), withPermit: mkYN(),
    withoutPermit: mkYN(), leased: mkYN(), shortTerm: mkYN(),
  },
  s8access: {
    access: mkYN(), encroachments: mkYN(), roads: mkYN(),
  },
  s8general: {
    reports: mkYN(), insurance: mkYN(), litigation: mkYN(), deed: mkYN(), historic: mkYN(),
  },
  adverseFacts: '',
  // Step 9
  s9hoa: {
    isHOA: mkYN(), assessments: mkYN(), defects: mkYN(), lawsuit: mkYN(),
  },
  hoa1Name: '', hoa1Contact: '', hoa2Name: '', hoa2Contact: '',
  s9metro: { inMetro: mkYN() },
  metro1Name: '', metro1Website: '',
  // Step 10
  sig1Name: '', sig1Date: '', sig2Name: '', sig2Date: '',
});

// ── Helpers ────────────────────────────────────────────────────────────────────

const getFirebaseToken = () => new Promise((resolve, reject) => {
  if (auth.currentUser) {
    auth.currentUser.getIdToken(true).then(resolve).catch(reject);
  } else {
    const unsub = onAuthStateChanged(auth, user => {
      unsub();
      if (user) user.getIdToken(true).then(resolve).catch(reject);
      else reject(new Error('No authenticated user'));
    });
  }
});

function SectionHead({ icon, text }) {
  return (
    <div className="spd-section-head">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2">{icon}</svg>
      <span className="spd-section-head-text">{text}</span>
    </div>
  );
}

function NoteBox({ children }) {
  return (
    <div className="spd-note-box">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <span>{children}</span>
    </div>
  );
}

function SubDiv({ children }) {
  return <div className="spd-sub-div">{children}</div>;
}

function DiscTable({ children, hasComment = true }) {
  return (
    <div className="spd-disc-table">
      <div className="spd-disc-thead">
        <div>Item</div>
        <div className="spd-disc-thead-center">Yes?</div>
        {hasComment && <div>Comments</div>}
      </div>
      {children}
    </div>
  );
}

function DiscRow({ text, val, onChange, hasComment = true, selectors }) {
  // selectors: optional [{ key, options }] — renders a pill group per entry (e.g. fuel, config)
  // writing the picked option to val[key]. Clicking a selected pill clears it.
  return (
    <div className="spd-disc-row" style={!hasComment ? { gridTemplateColumns: '1fr 96px' } : undefined}>
      <div className="spd-disc-text">{text}</div>
      <div className="spd-yn-wrap">
        <button type="button" className={`spd-yn-btn yes${val.yn === 'yes' ? ' sel' : ''}`} onClick={() => onChange({ ...val, yn: val.yn === 'yes' ? '' : 'yes' })}>Yes</button>
        <button type="button" className={`spd-yn-btn no${val.yn === 'no' ? ' sel' : ''}`} onClick={() => onChange({ ...val, yn: val.yn === 'no' ? '' : 'no' })}>No</button>
      </div>
      {hasComment && (
        (selectors && selectors.length) ? (
          <div className="spd-comment-cell">
            {selectors.map(sel => (
              <div className="spd-fuel-wrap" key={sel.key}>
                {sel.options.map(opt => (
                  <button type="button" key={opt}
                    className={`spd-fuel-btn${val[sel.key] === opt ? ' sel' : ''}`}
                    onClick={() => onChange({ ...val, [sel.key]: val[sel.key] === opt ? '' : opt })}>{opt}</button>
                ))}
              </div>
            ))}
            <input className="spd-comment" placeholder="Details..." value={val.comment} onChange={e => onChange({ ...val, comment: e.target.value })} />
          </div>
        ) : (
          <input className="spd-comment" placeholder="Details..." value={val.comment} onChange={e => onChange({ ...val, comment: e.target.value })} />
        )
      )}
    </div>
  );
}

function InfoTable({ children }) {
  return <div className="spd-info-table">{children}</div>;
}

function InfoRow({ label, children }) {
  return (
    <div className="spd-info-row">
      <div className="spd-info-label">{label}</div>
      <div className="spd-info-value">{children}</div>
    </div>
  );
}

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="spd-radio-group">
      {options.map(opt => (
        <label key={opt.value} className={`spd-radio-item${value === opt.value ? ' sel' : ''}`}>
          <input type="radio" name={name} value={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)} />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function CbGrid({ options, values, onChange }) {
  return (
    <div className="spd-cb-grid">
      {options.map(opt => {
        const checked = values.includes(opt);
        return (
          <label key={opt} className={`spd-cb-item${checked ? ' chk' : ''}`}>
            <input type="checkbox" checked={checked} onChange={() => {
              onChange(checked ? values.filter(v => v !== opt) : [...values, opt]);
            }} />
            {opt}
          </label>
        );
      })}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

function SPDWizardModal({ isOpen, onClose, onComplete, onReviewComplete, onProgressUpdate, readOnly, transaction, draftData, participants = [] }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [formData, setFormData] = useState(initForm());
  const [draftId, setDraftId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const upd = useCallback((key, val) => setFormData(prev => ({ ...prev, [key]: val })), []);
  const updNested = useCallback((group, key, val) => setFormData(prev => ({
    ...prev,
    [group]: { ...prev[group], [key]: val },
  })), []);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Pre-fill / restore from transaction and draftData whenever the modal opens
  useEffect(() => {
    if (!isOpen || !transaction) return;

    const yn = (bool, note) => ({ yn: bool === true ? 'yes' : bool === false ? 'no' : '', comment: note || '' });

    // Extract sellers from participants (all roles that are SELLER)
    const sellerParts = (participants || []).filter(p =>
      (p.role || p.participantRole || p.userRole || '').toUpperCase() === 'SELLER'
    );
    const sellerFullName = (p) => p
      ? (p.name || p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim())
      : '';
    const seller1 = sellerFullName(sellerParts[0]) || transaction?.sellerName || '';
    const seller2 = sellerFullName(sellerParts[1]) || '';
    const allSellersLabel = [seller1, seller2].filter(Boolean).join(' & ') || transaction?.sellerName || '';

    if (draftData) {
      const s = (n) => draftData.steps?.[String(n)]?.data || {};
      const d1 = s(1); const d2 = s(2); const d3 = s(3); const d4 = s(4);
      const d5 = s(5); const d6 = s(6); const d7 = s(7); const d8 = s(8);
      const d9 = s(9); const d10 = s(10);

      const settling = [];
      if (d2.settlingFoundations)   settling.push('Foundations');
      if (d2.settlingFloors)        settling.push('Floors');
      if (d2.settlingInteriorWalls) settling.push('Interior Walls');
      if (d2.settlingExteriorWalls) settling.push('Exterior Walls');
      if (d2.settlingDriveways)     settling.push('Driveways');
      if (d2.settlingSidewalks)     settling.push('Sidewalks');
      if (d2.settlingPatios)        settling.push('Patios');
      if (d2.settlingRetainingWalls) settling.push('Retaining Walls');

      setDraftId(draftData.id || draftData.wizardId || null);
      setCurrentStep(draftData.currentStep || 1);
      if (Array.isArray(draftData.completedSteps)) setCompletedSteps(new Set(draftData.completedSteps));
      setFormData({
        ...initForm(),
        // Step 1
        dateCompleted: d1.dateSpdCompleted || '',
        sellerNames:   d1.sellerNames || allSellersLabel,
        yearBuilt:     d1.yearBuilt || '',
        yearAcquired:  d1.yearAcquired || '',
        occupying:     d1.currentlyOccupying === true ? 'yes' : d1.currentlyOccupying === false ? 'no' : '',
        ifNotOccupying: d1.ifNotOccupying || '',
        // Step 2
        s2: {
          structural:        yn(d2.structuralProblems,   d2.structuralProblemsNote),
          structuralSupports: yn(d2.structuralSupports,  d2.structuralSupportsNote),
          moisture:           yn(d2.moistureLeakage,     d2.moistureLeakageNote),
          termites:           yn(d2.termiteDamage,       d2.termiteDamageNote),
          hailWind:           yn(d2.hailWindDamage,      d2.hailWindDamageNote),
          windowLeaks:        yn(d2.windowLeaks,         d2.windowLeaksNote),
          eifs:               yn(d2.eifs,                d2.eifsNote),
          subfloor:           yn(d2.subfloorProblems,    d2.subfloorProblemsNote),
        },
        settling,
        settlingComment: d2.settlingComments || '',
        // Step 3
        roofAge:         d3.roofAge || '',
        roofMaterial:    d3.roofMaterial || '',
        roofWarranty:    d3.roofWarranty === true ? 'yes' : d3.roofWarranty === false ? 'no' : '',
        warrantyExp:     d3.warrantyExpiration || '',
        warrantyTransfer: d3.warrantyTransferable || '',
        s3roof: {
          leak:    yn(d3.roofLeak,      d3.roofLeakNote),
          damage:  yn(d3.roofDamage,    d3.roofDamageNote),
          skylight: yn(d3.skylightDamage, d3.skylightDamageNote),
          gutter:  yn(d3.gutterDamage,  d3.gutterDamageNote),
        },
        s3app: {
          dryer:       { yn: d3.clothesDryer === true ? 'yes' : d3.clothesDryer === false ? 'no' : '', comment: '', type: d3.clothesDryerType || '' },
          dishwasher:  yn(d3.dishwasher,    d3.dishwasherAge),
          refrigerator: yn(d3.refrigerator, d3.refrigeratorAge),
          oven:        { yn: d3.ovenRange === true ? 'yes' : d3.ovenRange === false ? 'no' : '', comment: d3.ovenNote || '', fuel: d3.ovenFuel || '', config: d3.ovenConfig || '' },
          range:       { yn: d3.rangeStove === true ? 'yes' : d3.rangeStove === false ? 'no' : '', comment: d3.rangeNote || '', fuel: d3.rangeFuel || '', config: d3.rangeConfig || '' },
          microwave:   { yn: d3.microwave === true ? 'yes' : d3.microwave === false ? 'no' : '', comment: d3.microwaveType || '', type: d3.microwaveType || '' },
          vacuum:      yn(d3.builtInVacuum,  d3.builtInVacuumNote),
          trash:       yn(d3.trashCompactor, d3.trashCompactorNote),
        },
        // Step 4
        electricAmps:      d4.electricalAmps || '',
        electricProvider:  d4.electricProvider || '',
        cableProvider:     d4.cableTvProvider || '',
        internetProvider:  d4.internetProvider || '',
        internetType:      Array.isArray(d4.internetWiring) ? (d4.internetWiring[0]?.toLowerCase() || '') : (d4.internetWiring || ''),
        solarPanels:       d4.solarPanels || '',
        securitySystem:    d4.securitySystem || '',
        s4now: {
          security: yn(d4.securitySystemProblem, d4.securitySystemNote),
          smoke:    yn(d4.smokeDetectors,         d4.smokeDetectorsNote),
          carbon:   yn(d4.coAlarms,               d4.coAlarmsNote),
          fixtures: yn(d4.lightFixtures,           d4.lightFixturesNote),
          garage:   yn(d4.garageDoorOpener,        d4.garageDoorOpenerNote),
        },
        s4ever: {
          service:  yn(d4.electricalServiceProblems, d4.electricalServiceNote),
          aluminum: yn(d4.aluminumWiring,            d4.aluminumWiringNote),
          solar:    yn(d4.solarPanelProblems,        d4.solarPanelNote),
          panel:    yn(d4.wiringPanelProblems,       d4.wiringPanelNote),
        },
        // Step 5
        furnaceType:    d5.furnaceType || '',
        furnaceUnits:   d5.furnaceUnits || '',
        fireplaceTypes: Array.isArray(d5.fireplaceTypes) ? d5.fireplaceTypes : [],
        lastChimney:    d5.lastChimneyCleaned || '',
        fuelTanks:      d5.fuelTanks || '',
        acType:         d5.acType || '',
        acUnits:        d5.acUnits || '',
        s5: {
          furnace:    yn(d5.furnaceProblem,    d5.furnaceNote),
          centralAC:  yn(d5.centralAcProblem,  d5.centralAcNote),
          heatPump:   yn(d5.heatPumpProblem,   d5.heatPumpNote),
          evaporative: yn(d5.evaporativeCooler, d5.evaporativeCoolerNote),
          fireplace:       yn(d5.fireplaceProblem,       d5.fireplaceNote),
          fireplaceInsert: yn(d5.fireplaceInsertProblem, d5.fireplaceInsertNote),
          heatingstove:    yn(d5.heatingStoveProblem,    d5.heatingStoveNote),
          fuelTanksP: yn(d5.fuelTankProblem,   d5.fuelTankNote),
          sumpPump:   yn(d5.sumpPumps,         d5.sumpPumpsNote),
          garageDoors: yn(d5.garageDoors,      d5.garageDoorsNote),
        },
        // Step 6
        waterSupplyType:   Array.isArray(d6.waterSupplyTypes) ? (d6.waterSupplyTypes[0]?.toLowerCase() || '') : (d6.waterSupplyTypes || ''),
        wellPermit:        d6.wellPermit || '',
        drillingRecords:   d6.drillingRecords === true ? 'yes' : d6.drillingRecords === false ? 'no' : '',
        waterProvider:     d6.waterProviderName || '',
        waterProviderPhone: d6.waterProviderPhone || '',
        waterHeaters:      d6.waterHeaters || '',
        waterFilter:       d6.waterFilter || '',
        waterSoftener:     d6.waterSoftener || '',
        s6: {
          leaks:     yn(d6.plumbingProblems,  d6.plumbingNote),
          well:      yn(d6.wellProblems,      d6.wellNote),
          heater:    yn(d6.waterHeaterProblem, d6.waterHeaterNote),
          sprinkler: yn(d6.sprinklerProblems, d6.sprinklerNote),
          potability: yn(d6.waterTested,      d6.waterTestedNote),
          sewer:     yn(d6.sewerSepticLeaks,  d6.sewerSepticNote),
        },
        sewerType:       Array.isArray(d6.sewerServiceTypes) ? (d6.sewerServiceTypes[0]?.toLowerCase() || '') : (d6.sewerServiceTypes || ''),
        sewerLastScoped: d6.sewerLineScoped || '',
        septicLastPumped: d6.septicPumped || '',
        // Step 7
        s7flood: {
          flooding:  yn(d7.flooding,             d7.floodingNote),
          drainage:  yn(d7.drainageProblems,     d7.drainageNote),
          intrusion: yn(d7.waterIntrusion,       d7.waterIntrusionNote),
          repairs:   yn(d7.waterIntrusionRepairs, d7.waterIntrusionRepairsNote),
          ponds:     yn(d7.retentionPonds,       d7.retentionPondsNote),
        },
        s7env: {
          hazardous: yn(d7.hazardousMaterials, d7.hazardousNote),
          tanks:     yn(d7.storageTanks,       d7.storageTanksNote),
          expansive: yn(d7.expansiveSoils,     d7.expansiveSoilsNote),
          floodplain: yn(d7.floodplain,        d7.floodplainNote),
          oilGas:    yn(d7.oilGasWells,        d7.oilGasNote),
          meth:      yn(d7.methLab,            d7.methLabNote),
        },
        s7radon: {
          tested:     yn(d7.radonTested,      d7.radonTestedNote),
          detected:   yn(d7.radonDetected,    d7.radonDetectedNote),
          mitigation: yn(d7.radonMitigation,  d7.radonMitigationNote),
        },
        // Step 8
        s8legal: {
          zoning:       yn(d8.zoningViolation,         d8.zoningNote),
          condemnation: yn(d8.condemnation,             d8.condemnationNote),
          buildingCode: yn(d8.buildingCodeViolations,   d8.buildingCodeNote),
          withPermit:   yn(d8.alterationsWithPermit,    d8.alterationsWithPermitNote),
          withoutPermit: yn(d8.alterationsWithoutPermit, d8.alterationsWithoutPermitNote),
          leased:       yn(d8.propertyLeased,           d8.propertyLeasedNote),
          shortTerm:    yn(d8.shortTermRentals,         d8.shortTermRentalsNote),
        },
        s8access: {
          access:        yn(d8.accessProblems,  d8.accessNote),
          encroachments: yn(d8.encroachments,   d8.encroachmentNote),
          roads:         yn(d8.roadsUsedByOthers, d8.roadsNote),
        },
        s8general: {
          reports:    yn(d8.writtenReports,   d8.writtenReportsNote),
          insurance:  yn(d8.insuranceClaim,   d8.insuranceClaimNote),
          litigation: yn(d8.pendingLitigation, d8.litigationNote),
          deed:       yn(d8.deedRestrictions, d8.deedRestrictionsNote),
          historic:   yn(d8.historicDistrict, d8.historicDistrictNote),
        },
        adverseFacts: d8.otherAdverseFacts || '',
        // Step 9
        s9hoa: {
          isHOA:       yn(d9.hoaMember,            undefined),
          assessments: yn(d9.specialAssessments,   d9.specialAssessmentsNote),
          defects:     yn(d9.commonElementDefects, d9.commonElementNote),
          lawsuit:     yn(d9.hoaLawsuit,           d9.hoaLawsuitNote),
        },
        hoa1Name:    d9.hoa1Name || '',
        hoa1Contact: d9.hoa1Contact || '',
        hoa2Name:    d9.hoa2Name || '',
        hoa2Contact: d9.hoa2Contact || '',
        s9metro: { inMetro: yn(d9.metroDistrict, undefined) },
        metro1Name:    d9.metroDistrict1Name || d9.metroDistrictName || '',
        metro1Website: d9.metroDistrictWebsite || '',
        // Step 10
        sig1Name: d10.seller1Name || seller1,
        sig1Date: d10.seller1Date || '',
        sig2Name: d10.seller2Name || seller2,
        sig2Date: d10.seller2Date || '',
      });
    }

    // Fallback: pre-fill from transaction when no draft exists (fresh launch)
    if (!draftData) {
      // Viewer's own profile-zone "today", not UTC's — .toISOString() reads
      // as UTC regardless of device/profile zone, which can be off by a day.
      const todayStr = nowWallClockInViewerZone().localDate;
      setCurrentStep(1);
      setDraftId(null);
      setCompletedSteps(new Set());
      setFormData({
        ...initForm(),
        sellerNames:   allSellersLabel,
        sig1Name:      seller1,
        sig2Name:      seller2,
        dateCompleted: todayStr,
        sig1Date:      todayStr,
      });
    }

    setSubmitted(false);
    setSaveError(null);
  }, [isOpen, transaction, draftData, participants]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const propAddress = [
    transaction?.propertyAddress,
    transaction?.propertyCity,
    transaction?.propertyState,
    transaction?.propertyZip,
  ].filter(Boolean).join(', ') || 'Property address';

  // ── Step payload builders (internal state → API format) ───────────────────
  const buildStepPayload = (stepNum) => {
    const b = (v) => v === 'yes';
    switch (stepNum) {
      case 1: return {
        propertyAddress: propAddress,
        dateSpdCompleted: formData.dateCompleted,
        sellerNames: formData.sellerNames,
        yearBuilt: formData.yearBuilt,
        yearAcquired: formData.yearAcquired,
        currentlyOccupying: b(formData.occupying),
        ifNotOccupying: formData.ifNotOccupying,
      };
      case 2: return {
        structuralProblems: b(formData.s2.structural.yn),        structuralProblemsNote: formData.s2.structural.comment,
        structuralSupports: b(formData.s2.structuralSupports.yn), structuralSupportsNote: formData.s2.structuralSupports.comment,
        moistureLeakage: b(formData.s2.moisture.yn),             moistureLeakageNote: formData.s2.moisture.comment,
        termiteDamage: b(formData.s2.termites.yn),               termiteDamageNote: formData.s2.termites.comment,
        hailWindDamage: b(formData.s2.hailWind.yn),              hailWindDamageNote: formData.s2.hailWind.comment,
        windowLeaks: b(formData.s2.windowLeaks.yn),              windowLeaksNote: formData.s2.windowLeaks.comment,
        eifs: b(formData.s2.eifs.yn),                            eifsNote: formData.s2.eifs.comment,
        subfloorProblems: b(formData.s2.subfloor.yn),            subfloorProblemsNote: formData.s2.subfloor.comment,
        settlingFoundations: formData.settling.includes('Foundations'),
        settlingFloors: formData.settling.includes('Floors'),
        settlingInteriorWalls: formData.settling.includes('Interior Walls'),
        settlingExteriorWalls: formData.settling.includes('Exterior Walls'),
        settlingDriveways: formData.settling.includes('Driveways'),
        settlingSidewalks: formData.settling.includes('Sidewalks'),
        settlingPatios: formData.settling.includes('Patios'),
        settlingRetainingWalls: formData.settling.includes('Retaining Walls'),
        settlingComments: formData.settlingComment,
      };
      case 3: return {
        roofAge: formData.roofAge, roofMaterial: formData.roofMaterial,
        roofWarranty: b(formData.roofWarranty), warrantyExpiration: formData.warrantyExp, warrantyTransferable: formData.warrantyTransfer,
        roofLeak: b(formData.s3roof.leak.yn),        roofLeakNote: formData.s3roof.leak.comment,
        roofDamage: b(formData.s3roof.damage.yn),    roofDamageNote: formData.s3roof.damage.comment,
        skylightDamage: b(formData.s3roof.skylight.yn), skylightDamageNote: formData.s3roof.skylight.comment,
        gutterDamage: b(formData.s3roof.gutter.yn),  gutterDamageNote: formData.s3roof.gutter.comment,
        clothesDryer: b(formData.s3app.dryer.yn),    clothesDryerType: formData.s3app.dryer.type || '',
        dishwasher: b(formData.s3app.dishwasher.yn), dishwasherAge: formData.s3app.dishwasher.comment,
        refrigerator: b(formData.s3app.refrigerator.yn), refrigeratorAge: formData.s3app.refrigerator.comment,
        ovenRange: b(formData.s3app.oven.yn),        ovenFuel: formData.s3app.oven.fuel || '', ovenConfig: formData.s3app.oven.config || '', ovenNote: formData.s3app.oven.comment,
        rangeStove: b(formData.s3app.range.yn),      rangeFuel: formData.s3app.range.fuel || '', rangeConfig: formData.s3app.range.config || '', rangeNote: formData.s3app.range.comment,
        microwave: b(formData.s3app.microwave.yn),   microwaveType: formData.s3app.microwave.type || formData.s3app.microwave.comment,
        builtInVacuum: b(formData.s3app.vacuum.yn),  builtInVacuumNote: formData.s3app.vacuum.comment,
        trashCompactor: b(formData.s3app.trash.yn),  trashCompactorNote: formData.s3app.trash.comment,
      };
      case 4: return {
        electricalAmps: formData.electricAmps, electricProvider: formData.electricProvider,
        cableTvProvider: formData.cableProvider, internetProvider: formData.internetProvider,
        internetWiring: formData.internetType ? [formData.internetType] : [],
        solarPanels: formData.solarPanels, securitySystem: formData.securitySystem,
        securitySystemProblem: b(formData.s4now.security.yn), securitySystemNote: formData.s4now.security.comment,
        smokeDetectors: b(formData.s4now.smoke.yn),    smokeDetectorsNote: formData.s4now.smoke.comment,
        coAlarms: b(formData.s4now.carbon.yn),          coAlarmsNote: formData.s4now.carbon.comment,
        lightFixtures: b(formData.s4now.fixtures.yn),   lightFixturesNote: formData.s4now.fixtures.comment,
        garageDoorOpener: b(formData.s4now.garage.yn),  garageDoorOpenerNote: formData.s4now.garage.comment,
        electricalServiceProblems: b(formData.s4ever.service.yn),  electricalServiceNote: formData.s4ever.service.comment,
        aluminumWiring: b(formData.s4ever.aluminum.yn), aluminumWiringNote: formData.s4ever.aluminum.comment,
        solarPanelProblems: b(formData.s4ever.solar.yn), solarPanelNote: formData.s4ever.solar.comment,
        wiringPanelProblems: b(formData.s4ever.panel.yn), wiringPanelNote: formData.s4ever.panel.comment,
      };
      case 5: return {
        furnaceType: formData.furnaceType, furnaceUnits: formData.furnaceUnits,
        fireplaceTypes: formData.fireplaceTypes, lastChimneyCleaned: formData.lastChimney,
        fuelTanks: formData.fuelTanks, acType: formData.acType, acUnits: formData.acUnits,
        furnaceProblem: b(formData.s5.furnace.yn),     furnaceNote: formData.s5.furnace.comment,
        centralAcProblem: b(formData.s5.centralAC.yn), centralAcNote: formData.s5.centralAC.comment,
        heatPumpProblem: b(formData.s5.heatPump.yn),   heatPumpNote: formData.s5.heatPump.comment,
        evaporativeCooler: b(formData.s5.evaporative.yn), evaporativeCoolerNote: formData.s5.evaporative.comment,
        fireplaceProblem: b(formData.s5.fireplace.yn),             fireplaceNote: formData.s5.fireplace.comment,
        fireplaceInsertProblem: b(formData.s5.fireplaceInsert.yn), fireplaceInsertNote: formData.s5.fireplaceInsert.comment,
        heatingStoveProblem: b(formData.s5.heatingstove.yn),       heatingStoveNote: formData.s5.heatingstove.comment,
        fuelTankProblem: b(formData.s5.fuelTanksP.yn), fuelTankNote: formData.s5.fuelTanksP.comment,
        sumpPumps: b(formData.s5.sumpPump.yn),         sumpPumpsNote: formData.s5.sumpPump.comment,
        garageDoors: b(formData.s5.garageDoors.yn),    garageDoorsNote: formData.s5.garageDoors.comment,
      };
      case 6: return {
        waterSupplyTypes: formData.waterSupplyType ? [formData.waterSupplyType] : [],
        wellPermit: formData.wellPermit, drillingRecords: b(formData.drillingRecords),
        waterProviderName: formData.waterProvider, waterProviderPhone: formData.waterProviderPhone,
        waterHeaters: formData.waterHeaters, waterFilter: formData.waterFilter, waterSoftener: formData.waterSoftener,
        plumbingProblems: b(formData.s6.leaks.yn),     plumbingNote: formData.s6.leaks.comment,
        wellProblems: b(formData.s6.well.yn),           wellNote: formData.s6.well.comment,
        waterHeaterProblem: b(formData.s6.heater.yn),  waterHeaterNote: formData.s6.heater.comment,
        sprinklerProblems: b(formData.s6.sprinkler.yn), sprinklerNote: formData.s6.sprinkler.comment,
        waterTested: b(formData.s6.potability.yn),      waterTestedNote: formData.s6.potability.comment,
        sewerSepticLeaks: b(formData.s6.sewer.yn),     sewerSepticNote: formData.s6.sewer.comment,
        sewerServiceTypes: formData.sewerType ? [formData.sewerType] : [],
        sewerLineScoped: formData.sewerLastScoped, septicPumped: formData.septicLastPumped,
      };
      case 7: return {
        flooding: b(formData.s7flood.flooding.yn),     floodingNote: formData.s7flood.flooding.comment,
        drainageProblems: b(formData.s7flood.drainage.yn), drainageNote: formData.s7flood.drainage.comment,
        waterIntrusion: b(formData.s7flood.intrusion.yn), waterIntrusionNote: formData.s7flood.intrusion.comment,
        waterIntrusionRepairs: b(formData.s7flood.repairs.yn), waterIntrusionRepairsNote: formData.s7flood.repairs.comment,
        retentionPonds: b(formData.s7flood.ponds.yn),  retentionPondsNote: formData.s7flood.ponds.comment,
        hazardousMaterials: b(formData.s7env.hazardous.yn), hazardousNote: formData.s7env.hazardous.comment,
        storageTanks: b(formData.s7env.tanks.yn),      storageTanksNote: formData.s7env.tanks.comment,
        expansiveSoils: b(formData.s7env.expansive.yn), expansiveSoilsNote: formData.s7env.expansive.comment,
        floodplain: b(formData.s7env.floodplain.yn),   floodplainNote: formData.s7env.floodplain.comment,
        oilGasWells: b(formData.s7env.oilGas.yn),      oilGasNote: formData.s7env.oilGas.comment,
        methLab: b(formData.s7env.meth.yn),            methLabNote: formData.s7env.meth.comment,
        radonTested: b(formData.s7radon.tested.yn),    radonTestedNote: formData.s7radon.tested.comment,
        radonDetected: b(formData.s7radon.detected.yn), radonDetectedNote: formData.s7radon.detected.comment,
        radonMitigation: b(formData.s7radon.mitigation.yn), radonMitigationNote: formData.s7radon.mitigation.comment,
      };
      case 8: return {
        zoningViolation: b(formData.s8legal.zoning.yn),          zoningNote: formData.s8legal.zoning.comment,
        condemnation: b(formData.s8legal.condemnation.yn),        condemnationNote: formData.s8legal.condemnation.comment,
        buildingCodeViolations: b(formData.s8legal.buildingCode.yn), buildingCodeNote: formData.s8legal.buildingCode.comment,
        alterationsWithPermit: b(formData.s8legal.withPermit.yn), alterationsWithPermitNote: formData.s8legal.withPermit.comment,
        alterationsWithoutPermit: b(formData.s8legal.withoutPermit.yn), alterationsWithoutPermitNote: formData.s8legal.withoutPermit.comment,
        propertyLeased: b(formData.s8legal.leased.yn),            propertyLeasedNote: formData.s8legal.leased.comment,
        shortTermRentals: b(formData.s8legal.shortTerm.yn),       shortTermRentalsNote: formData.s8legal.shortTerm.comment,
        accessProblems: b(formData.s8access.access.yn),           accessNote: formData.s8access.access.comment,
        encroachments: b(formData.s8access.encroachments.yn),     encroachmentNote: formData.s8access.encroachments.comment,
        roadsUsedByOthers: b(formData.s8access.roads.yn),         roadsNote: formData.s8access.roads.comment,
        writtenReports: b(formData.s8general.reports.yn),         writtenReportsNote: formData.s8general.reports.comment,
        insuranceClaim: b(formData.s8general.insurance.yn),       insuranceClaimNote: formData.s8general.insurance.comment,
        pendingLitigation: b(formData.s8general.litigation.yn),   litigationNote: formData.s8general.litigation.comment,
        deedRestrictions: b(formData.s8general.deed.yn),          deedRestrictionsNote: formData.s8general.deed.comment,
        historicDistrict: b(formData.s8general.historic.yn),      historicDistrictNote: formData.s8general.historic.comment,
        otherAdverseFacts: formData.adverseFacts,
      };
      case 9: return {
        hoaMember: b(formData.s9hoa.isHOA.yn),
        hoaName: formData.hoa1Name,
        specialAssessments: b(formData.s9hoa.assessments.yn), specialAssessmentsNote: formData.s9hoa.assessments.comment,
        commonElementDefects: b(formData.s9hoa.defects.yn),   commonElementNote: formData.s9hoa.defects.comment,
        hoaLawsuit: b(formData.s9hoa.lawsuit.yn),             hoaLawsuitNote: formData.s9hoa.lawsuit.comment,
        hoa1Name: formData.hoa1Name, hoa1Contact: formData.hoa1Contact,
        hoa2Name: formData.hoa2Name, hoa2Contact: formData.hoa2Contact,
        metroDistrict: b(formData.s9metro.inMetro.yn),
        metroDistrictName: formData.metro1Name, metroDistrict1Name: formData.metro1Name,
        metroDistrictWebsite: formData.metro1Website,
      };
      case 10: return {
        seller1Name: formData.sig1Name, seller1Date: formData.sig1Date,
        seller2Name: formData.sig2Name, seller2Date: formData.sig2Date,
        confirmed: true,
      };
      default: return {};
    }
  };

  // ── Persist step to backend (best-effort) ─────────────────────────────────
  const persistStep = async (stepNum, isComplete) => {
    if (!transaction?.id) return;
    try {
      const token = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${token}` };
      let id = draftId;
      if (!id) {
        const res = await apiClient.post(API_ENDPOINTS.spdWizardDrafts(transaction.id), {}, { headers });
        id = res.data?.id || res.data?.wizardId || res.data?.draftId;
        if (id) setDraftId(id);
      }
      if (id) {
        await apiClient.put(
          API_ENDPOINTS.spdWizardSaveStep(transaction.id, id, stepNum, isComplete),
          { data: buildStepPayload(stepNum) },
          { headers }
        );
      }
    } catch {
      // silent — form data is held in state
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = async () => {
    if (readOnly) {
      setCurrentStep(s => s + 1);
      return;
    }
    const newCompleted = new Set(completedSteps);
    newCompleted.add(currentStep);
    setCompletedSteps(newCompleted);
    if (onProgressUpdate) onProgressUpdate(Array.from(newCompleted), currentStep + 1);
    await persistStep(currentStep, true);
    setCurrentStep(s => s + 1);
    setSaveError(null);
  };

  const handlePrev = () => setCurrentStep(s => s - 1);

  const handleCompleteReview = async () => {
    setIsSaving(true);
    try {
      if (onReviewComplete) await onReviewComplete();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const token = await getFirebaseToken();
      const headers = { Authorization: `Bearer ${token}` };
      let id = draftId;
      if (!id && transaction?.id) {
        const res = await apiClient.post(API_ENDPOINTS.spdWizardDrafts(transaction.id), {}, { headers });
        id = res.data?.id || res.data?.wizardId || res.data?.data?.id;
        if (id) setDraftId(id);
      }
      if (id && transaction?.id) {
        // Save step 10 (Review & Confirmation) with its payload
        await apiClient.put(
          API_ENDPOINTS.spdWizardSaveStep(transaction.id, id, 10, true),
          { data: buildStepPayload(10) },
          { headers }
        );
        // Submit — body must be empty per API spec
        await apiClient.post(
          API_ENDPOINTS.spdWizardSubmit(transaction.id, id),
          {},
          { headers }
        );
      }
      const allSteps = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);
      setCompletedSteps(new Set(allSteps));
      if (onProgressUpdate) onProgressUpdate(allSteps, TOTAL_STEPS);
      setSubmitted(true);
      if (onComplete) onComplete();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err?.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e) => { if (e.target === e.currentTarget) onClose(); };

  // ── Step renderers ─────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <div>
      <h2 className="rpa-step-heading">Property Information</h2>
      <div className="rpa-card">
        <NoteBox>
          This SPD should be completed by Seller, not by Broker, based on Seller's current actual knowledge.
          Seller must disclose all known adverse material facts.
        </NoteBox>
        <InfoTable>
          <InfoRow label="Property Address">
            <span className="spd-info-static">{propAddress}</span>
          </InfoRow>
          <InfoRow label="Date SPD Completed">
            <input className="spd-info-input" type="date" value={formData.dateCompleted} onChange={e => upd('dateCompleted', e.target.value)} />
          </InfoRow>
          <InfoRow label="Seller Name(s)">
            {formData.sellerNames
              ? <span className="spd-info-static">{formData.sellerNames}</span>
              : <input className="spd-info-input" type="text" placeholder="As it appears on title" value={formData.sellerNames} onChange={e => upd('sellerNames', e.target.value)} />}
          </InfoRow>
          <InfoRow label="Year Built">
            <input className="spd-info-input" type="text" placeholder="e.g. 1998" style={{ maxWidth: 120 }} value={formData.yearBuilt} onChange={e => upd('yearBuilt', e.target.value)} />
          </InfoRow>
          <InfoRow label="Year Seller Acquired">
            <input className="spd-info-input" type="text" placeholder="e.g. 2015" style={{ maxWidth: 120 }} value={formData.yearAcquired} onChange={e => upd('yearAcquired', e.target.value)} />
          </InfoRow>
          <InfoRow label="Currently Occupying?">
            <RadioGroup name="s1_occ" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.occupying} onChange={v => upd('occupying', v)} />
          </InfoRow>
          {formData.occupying === 'no' && (
            <InfoRow label="If not occupying — property was">
              <RadioGroup name="s1_vacant" options={[{ value: 'vacant', label: 'Vacant' }, { value: 'occupied', label: 'Occupied by others' }]} value={formData.ifNotOccupying} onChange={v => upd('ifNotOccupying', v)} />
            </InfoRow>
          )}
        </InfoTable>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const s2 = formData.s2;
    const rows = [
      ['structural', 'Structural problems with improvements'],
      ['structuralSupports', 'Structural supports or reinforcements added'],
      ['moisture', 'Moisture / water leakage in basement or crawlspace'],
      ['termites', 'Damage due to termites, insects, birds, animals or rodents'],
      ['hailWind', 'Damage due to hail, wind, fire, flood or other casualty'],
      ['windowLeaks', 'Window leaks'],
      ['eifs', 'Exterior Artificial Stucco (EIFS)'],
      ['subfloor', 'Subfloor problems'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">Building Conditions</h2>
        <div className="rpa-card">
          <SectionHead icon={<><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></>} text="A. Building Conditions — Problems Ever Existing" />
          <DiscTable>
            {rows.map(([key, text]) => (
              <DiscRow key={key} text={text} val={s2[key]} onChange={v => updNested('s2', key, v)} />
            ))}
          </DiscTable>
          <SubDiv>Settling / Movement / Cracking — check all that apply</SubDiv>
          <CbGrid
            options={['Foundations', 'Floors', 'Interior Walls', 'Exterior Walls', 'Driveways', 'Sidewalks', 'Patios', 'Retaining Walls']}
            values={formData.settling}
            onChange={v => upd('settling', v)}
          />
          <div style={{ marginTop: 10 }}>
            <input className="spd-info-input" type="text" placeholder="Comments on settling / movement..." value={formData.settlingComment} onChange={e => upd('settlingComment', e.target.value)} style={{ width: '100%' }} />
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    const roofRows = [
      ['leak', 'Roof leak'],
      ['damage', 'Damage to roof'],
      ['skylight', 'Damage to skylight'],
      ['gutter', 'Damage to gutter or downspout'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">Roof & Appliances</h2>
        <div className="rpa-card">
          <SectionHead icon={<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>} text="B. Roof — General Information" />
          <InfoTable>
            <InfoRow label="Age of Roof (years)">
              <input className="spd-info-input" type="text" placeholder="e.g. 8" style={{ maxWidth: 100 }} value={formData.roofAge} onChange={e => upd('roofAge', e.target.value)} />
            </InfoRow>
            <InfoRow label="Roof Material">
              <input className="spd-info-input" type="text" placeholder="e.g. Asphalt shingle, metal, tile" value={formData.roofMaterial} onChange={e => upd('roofMaterial', e.target.value)} />
            </InfoRow>
            <InfoRow label="Roof Under Warranty?">
              <RadioGroup name="s3_rw" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.roofWarranty} onChange={v => upd('roofWarranty', v)} />
            </InfoRow>
            {formData.roofWarranty === 'yes' && <>
              <InfoRow label="Warranty Expiration">
                <input className="spd-info-input" type="date" value={formData.warrantyExp} onChange={e => upd('warrantyExp', e.target.value)} />
              </InfoRow>
              <InfoRow label="Warranty Transferable?">
                <RadioGroup name="s3_rt" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unknown', label: 'Unknown' }]} value={formData.warrantyTransfer} onChange={v => upd('warrantyTransfer', v)} />
              </InfoRow>
            </>}
          </InfoTable>
          <SectionHead icon={<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>} text="B. Roof — Problems Ever Existing" />
          <DiscTable>
            {roofRows.map(([key, text]) => (
              <DiscRow key={key} text={text} val={formData.s3roof[key]} onChange={v => updNested('s3roof', key, v)} />
            ))}
          </DiscTable>
          <SectionHead icon={<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>} text="C. Appliances — Problems Now Existing (if included in sale)" />
          <DiscTable>
            <DiscRow text="Clothes dryer" selectors={[{ key: 'type', options: ['Gas', 'Electric'] }]} val={formData.s3app.dryer} onChange={v => updNested('s3app', 'dryer', v)} />
            <DiscRow text="Dishwasher / Disposal" val={formData.s3app.dishwasher} onChange={v => updNested('s3app', 'dishwasher', v)} />
            <DiscRow text="Refrigerator" val={formData.s3app.refrigerator} onChange={v => updNested('s3app', 'refrigerator', v)} />
            <DiscRow text="Oven" selectors={[{ key: 'fuel', options: ['Gas', 'Electric'] }, { key: 'config', options: ['Single', 'Double'] }]} val={formData.s3app.oven} onChange={v => updNested('s3app', 'oven', v)} />
            <DiscRow text="Range / Stove" selectors={[{ key: 'fuel', options: ['Gas', 'Electric'] }, { key: 'config', options: ['Free Standing', 'Drop-In'] }]} val={formData.s3app.range} onChange={v => updNested('s3app', 'range', v)} />
            <DiscRow text="Microwave" val={formData.s3app.microwave} onChange={v => updNested('s3app', 'microwave', v)} />
            <DiscRow text="Built-in vacuum system" val={formData.s3app.vacuum} onChange={v => updNested('s3app', 'vacuum', v)} />
            <DiscRow text="Trash compactor / other" val={formData.s3app.trash} onChange={v => updNested('s3app', 'trash', v)} />
          </DiscTable>
        </div>
      </div>
    );
  };

  const renderStep4 = () => {
    const nowRows = [
      ['security', 'Security system'],
      ['smoke', 'Smoke / fire detectors'],
      ['carbon', 'Carbon monoxide alarms'],
      ['fixtures', 'Light fixtures, switches & outlets'],
      ['garage', 'Garage door opener / keyless entry'],
    ];
    const everRows = [
      ['service', 'Electrical service problems'],
      ['aluminum', 'Aluminum wiring at outlets (110)'],
      ['solar', 'Solar panel problems'],
      ['panel', 'Electric wiring or panel problems'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">Electrical</h2>
        <div className="rpa-card">
          <SectionHead icon={<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>} text="D. Electrical & Telecommunications — General" />
          <InfoTable>
            <InfoRow label="Electrical Service (Amps)"><input className="spd-info-input" type="text" placeholder="e.g. 200" style={{ maxWidth: 120 }} value={formData.electricAmps} onChange={e => upd('electricAmps', e.target.value)} /></InfoRow>
            <InfoRow label="Electric Provider"><input className="spd-info-input" type="text" placeholder="Provider name" value={formData.electricProvider} onChange={e => upd('electricProvider', e.target.value)} /></InfoRow>
            <InfoRow label="Cable / TV Provider"><input className="spd-info-input" type="text" placeholder="Provider name" value={formData.cableProvider} onChange={e => upd('cableProvider', e.target.value)} /></InfoRow>
            <InfoRow label="Internet Provider"><input className="spd-info-input" type="text" placeholder="Provider name" value={formData.internetProvider} onChange={e => upd('internetProvider', e.target.value)} /></InfoRow>
            <InfoRow label="Internet Wiring Type">
              <RadioGroup name="s4_inet" options={[{ value: 'cable', label: 'Cable' }, { value: 'dsl', label: 'DSL' }, { value: 'satellite', label: 'Satellite' }, { value: 'fiber', label: 'Fiber' }]} value={formData.internetType} onChange={v => upd('internetType', v)} />
            </InfoRow>
            <InfoRow label="Solar Panels">
              <RadioGroup name="s4_solar" options={[{ value: 'owned', label: 'Owned' }, { value: 'leased', label: 'Leased' }, { value: 'none', label: 'None' }]} value={formData.solarPanels} onChange={v => upd('solarPanels', v)} />
            </InfoRow>
            <InfoRow label="Security System">
              <RadioGroup name="s4_sec" options={[{ value: 'owned', label: 'Owned' }, { value: 'leased', label: 'Leased' }, { value: 'none', label: 'None' }]} value={formData.securitySystem} onChange={v => upd('securitySystem', v)} />
            </InfoRow>
          </InfoTable>
          <SectionHead icon={<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>} text="D. Electrical — Problems Now Existing" />
          <DiscTable>{nowRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s4now[key]} onChange={v => updNested('s4now', key, v)} />)}</DiscTable>
          <SectionHead icon={<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>} text="D. Electrical — Problems Ever Existing" />
          <DiscTable>{everRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s4ever[key]} onChange={v => updNested('s4ever', key, v)} />)}</DiscTable>
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    const hvacRows = [
      ['furnace', 'Furnace'],
      ['centralAC', 'Central air conditioning'],
      ['heatPump', 'Heat pump'],
      ['evaporative', 'Evaporative cooler'],
      ['fireplace', 'Fireplace'],
      ['fireplaceInsert', 'Fireplace insert'],
      ['heatingstove', 'Heating stove'],
      ['fuelTanksP', 'Fuel tanks'],
      ['sumpPump', 'Sump pump(s)'],
      ['garageDoors', 'Overhead / garage doors'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">HVAC & Heat</h2>
        <div className="rpa-card">
          <SectionHead icon={<><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></>} text="E & F. Ventilation, Air & Heat — General" />
          <InfoTable>
            <InfoRow label="Furnace Type">
              <select className="spd-info-select" value={formData.furnaceType} onChange={e => upd('furnaceType', e.target.value)}>
                <option value="">Select type</option>
                {['Forced Air Gas', 'Forced Air Electric', 'Forced Air Propane', 'Radiant', 'Gravity Flow', 'Other'].map(o => <option key={o}>{o}</option>)}
              </select>
            </InfoRow>
            <InfoRow label="Number of Furnace Units"><input className="spd-info-input" type="text" placeholder="e.g. 1" style={{ maxWidth: 80 }} value={formData.furnaceUnits} onChange={e => upd('furnaceUnits', e.target.value)} /></InfoRow>
            <InfoRow label="Fireplace Type">
              <CbGrid options={['Masonry', 'Insert', 'Wood Burning', 'Direct Vent']} values={formData.fireplaceTypes} onChange={v => upd('fireplaceTypes', v)} />
            </InfoRow>
            <InfoRow label="Last Chimney Cleaned"><input className="spd-info-input" type="date" value={formData.lastChimney} onChange={e => upd('lastChimney', e.target.value)} /></InfoRow>
            <InfoRow label="Fuel Tanks">
              <RadioGroup name="s5_ft" options={[{ value: 'owned', label: 'Owned' }, { value: 'leased', label: 'Leased' }, { value: 'none', label: 'None' }]} value={formData.fuelTanks} onChange={v => upd('fuelTanks', v)} />
            </InfoRow>
            <InfoRow label="A/C Type">
              <select className="spd-info-select" value={formData.acType} onChange={e => upd('acType', e.target.value)}>
                <option value="">Select type</option>
                {['Central Air Electric', 'Evaporative Cooler', 'Window Units', 'Heat Pump', 'Other'].map(o => <option key={o}>{o}</option>)}
              </select>
            </InfoRow>
            <InfoRow label="Number of A/C Units"><input className="spd-info-input" type="text" placeholder="e.g. 1" style={{ maxWidth: 80 }} value={formData.acUnits} onChange={e => upd('acUnits', e.target.value)} /></InfoRow>
          </InfoTable>
          <SectionHead icon={<><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/></>} text="E & F. HVAC & Mechanical — Problems Now Existing" />
          <DiscTable>{hvacRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s5[key]} onChange={v => updNested('s5', key, v)} />)}</DiscTable>
        </div>
      </div>
    );
  };

  const renderStep6 = () => {
    const waterRows = [
      ['leaks', 'Leaks, backups or plumbing problems (ever)'],
      ['well', 'Well problems (ever)'],
      ['heater', 'Water heater problems (now existing)'],
      ['sprinkler', 'Underground sprinkler system problems'],
      ['potability', 'Water tested for potability (ever)'],
      ['sewer', 'Sewer/septic leaks or backups (ever)'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">Water & Sewer</h2>
        <div className="rpa-card">
          <SectionHead icon={<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>} text="H. Source of Water & Water Supply" />
          <InfoTable>
            <InfoRow label="Type of Water Supply">
              <RadioGroup name="s6_ws" options={[{ value: 'public', label: 'Public' }, { value: 'community', label: 'Community' }, { value: 'well', label: 'Well' }, { value: 'sharedWell', label: 'Shared Well' }, { value: 'other', label: 'Other' }]} value={formData.waterSupplyType} onChange={v => upd('waterSupplyType', v)} />
            </InfoRow>
            {formData.waterSupplyType === 'well' || formData.waterSupplyType === 'sharedWell' ? <>
              <InfoRow label="Well Permit #"><input className="spd-info-input" type="text" placeholder="Permit number" value={formData.wellPermit} onChange={e => upd('wellPermit', e.target.value)} /></InfoRow>
              <InfoRow label="Drilling Records Provided?"><RadioGroup name="s6_dr" options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={formData.drillingRecords} onChange={v => upd('drillingRecords', v)} /></InfoRow>
            </> : null}
            <InfoRow label="Water Provider Name"><input className="spd-info-input" type="text" placeholder="e.g. Colorado Springs Utilities" value={formData.waterProvider} onChange={e => upd('waterProvider', e.target.value)} /></InfoRow>
            <InfoRow label="Water Provider Phone"><input className="spd-info-input" type="text" placeholder="Phone number" value={formData.waterProviderPhone} onChange={e => upd('waterProviderPhone', e.target.value)} /></InfoRow>
            <InfoRow label="Water Heater(s)"><input className="spd-info-input" type="text" placeholder="# of units, fuel type, capacity" value={formData.waterHeaters} onChange={e => upd('waterHeaters', e.target.value)} /></InfoRow>
            <InfoRow label="Water Filter System"><RadioGroup name="s6_wf" options={[{ value: 'owned', label: 'Owned' }, { value: 'leased', label: 'Leased' }, { value: 'none', label: 'None' }]} value={formData.waterFilter} onChange={v => upd('waterFilter', v)} /></InfoRow>
            <InfoRow label="Water Softener"><RadioGroup name="s6_wso" options={[{ value: 'owned', label: 'Owned' }, { value: 'leased', label: 'Leased' }, { value: 'none', label: 'None' }]} value={formData.waterSoftener} onChange={v => upd('waterSoftener', v)} /></InfoRow>
          </InfoTable>
          <SectionHead icon={<path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>} text="G. Water Problems & I. Sewer / Septic" />
          <DiscTable>{waterRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s6[key]} onChange={v => updNested('s6', key, v)} />)}</DiscTable>
          <InfoTable style={{ marginTop: 10 }}>
            <InfoRow label="Sewer Service Type"><RadioGroup name="s6_sew" options={[{ value: 'public', label: 'Public' }, { value: 'community', label: 'Community' }, { value: 'septic', label: 'Septic' }, { value: 'other', label: 'Other' }]} value={formData.sewerType} onChange={v => upd('sewerType', v)} /></InfoRow>
            <InfoRow label="Sewer Line Last Scoped"><input className="spd-info-input" type="date" value={formData.sewerLastScoped} onChange={e => upd('sewerLastScoped', e.target.value)} /></InfoRow>
            {formData.sewerType === 'septic' && <InfoRow label="Septic Last Pumped"><input className="spd-info-input" type="date" value={formData.septicLastPumped} onChange={e => upd('septicLastPumped', e.target.value)} /></InfoRow>}
          </InfoTable>
        </div>
      </div>
    );
  };

  const renderStep7 = () => {
    const floodRows = [
      ['flooding', 'Flooding'],
      ['drainage', 'Drainage problems'],
      ['intrusion', 'Water intrusion in basement or crawl space'],
      ['repairs', 'Repairs made to control water intrusion'],
      ['ponds', 'Drainage / retention ponds or storm water basins'],
    ];
    const envRows = [
      ['hazardous', 'Hazardous materials (asbestos, pesticides, petroleum products etc.)'],
      ['tanks', 'Underground or aboveground storage tanks'],
      ['expansive', 'Expansive soils / mine shafts / abandoned wells'],
      ['floodplain', 'Within floodplain, wetland or geological hazard area'],
      ['oilGas', 'Oil and gas wells or mining operations on/near property'],
      ['meth', 'Property previously used as methamphetamine laboratory'],
    ];
    const radonRows = [
      ['tested', 'Radon test(s) conducted on the property'],
      ['detected', 'Radon concentrations detected or mitigation performed'],
      ['mitigation', 'Radon mitigation system currently installed'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">Flooding & Environment</h2>
        <div className="rpa-card">
          <SectionHead icon={<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/></>} text="J. Flooding & Drainage — Ever Existing" />
          <DiscTable>{floodRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s7flood[key]} onChange={v => updNested('s7flood', key, v)} />)}</DiscTable>
          <SectionHead icon={<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/></>} text="N. Environmental Conditions — Ever Existing" />
          <DiscTable>{envRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s7env[key]} onChange={v => updNested('s7env', key, v)} />)}</DiscTable>
          <SectionHead icon={<circle cx="12" cy="12" r="10"/>} text="O. Radon — Ever Existing (CO law requires disclosure)" />
          <NoteBox>Colorado law requires disclosure of known radon test results. See CDPHE.COLORADO.GOV/RADON for guidance.</NoteBox>
          <DiscTable>{radonRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s7radon[key]} onChange={v => updNested('s7radon', key, v)} />)}</DiscTable>
        </div>
      </div>
    );
  };

  const renderStep8 = () => {
    const legalRows = [
      ['zoning', 'Zoning violation, variance or non-conforming use'],
      ['condemnation', 'Notice or threat of condemnation proceedings'],
      ['buildingCode', 'Building code, city or county violations'],
      ['withPermit', 'Additions or alterations WITH a Building Permit'],
      ['withoutPermit', 'Additions or alterations WITHOUT a Building Permit'],
      ['leased', 'Property leased to others (written or oral)'],
      ['shortTerm', 'Used for short-term rentals in past year'],
    ];
    const accessRows = [
      ['access', 'Any access problems or concerns'],
      ['encroachments', 'Encroachments, boundary disputes or unrecorded easements'],
      ['roads', 'Roads, trails or driveways used by others'],
    ];
    const generalRows = [
      ['reports', 'Written reports of building, roofing, soils or engineering investigations'],
      ['insurance', 'Property insurance claim submitted'],
      ['litigation', 'Pending litigation or dispute resolution'],
      ['deed', 'Deed restrictions or Affordable Housing restrictions'],
      ['historic', 'Property located in a historic district'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">Legal & Access</h2>
        <div className="rpa-card">
          <SectionHead icon={<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>} text="L. Use, Zoning & Legal Issues — Ever Existing" />
          <DiscTable>{legalRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s8legal[key]} onChange={v => updNested('s8legal', key, v)} />)}</DiscTable>
          <SectionHead icon={<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>} text="M. Access & Parking — Ever Existing" />
          <DiscTable>{accessRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s8access[key]} onChange={v => updNested('s8access', key, v)} />)}</DiscTable>
          <SectionHead icon={<><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></>} text="R. General Disclosures — Ever Existing" />
          <DiscTable>{generalRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s8general[key]} onChange={v => updNested('s8general', key, v)} />)}</DiscTable>
          <SubDiv>Other Known Adverse Material Facts</SubDiv>
          <textarea className="spd-textarea" rows={3} placeholder="Describe any other known adverse material facts affecting the property or its occupants..." value={formData.adverseFacts} onChange={e => upd('adverseFacts', e.target.value)} />
        </div>
      </div>
    );
  };

  const renderStep9 = () => {
    const hoaRows = [
      ['isHOA', 'Property is part of an owners\' association (HOA)'],
      ['assessments', 'Special assessments approved but not yet implemented'],
      ['defects', 'Problems or defects in common elements of the Association'],
      ['lawsuit', 'Association has commenced lawsuit against builder (ever)'],
    ];
    return (
      <div>
        <h2 className="rpa-step-heading">HOA & Metro District</h2>
        <div className="rpa-card">
          <SectionHead icon={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></>} text="P. Common Interest Community / HOA" />
          <DiscTable>{hoaRows.map(([key, text]) => <DiscRow key={key} text={text} val={formData.s9hoa[key]} onChange={v => updNested('s9hoa', key, v)} />)}</DiscTable>
          <InfoTable>
            <InfoRow label="HOA #1 Name"><input className="spd-info-input" type="text" placeholder="Association name" value={formData.hoa1Name} onChange={e => upd('hoa1Name', e.target.value)} /></InfoRow>
            <InfoRow label="HOA #1 Contact"><input className="spd-info-input" type="text" placeholder="Phone / website" value={formData.hoa1Contact} onChange={e => upd('hoa1Contact', e.target.value)} /></InfoRow>
            <InfoRow label="HOA #2 Name (if any)"><input className="spd-info-input" type="text" placeholder="Association name" value={formData.hoa2Name} onChange={e => upd('hoa2Name', e.target.value)} /></InfoRow>
            <InfoRow label="HOA #2 Contact"><input className="spd-info-input" type="text" placeholder="Phone / website" value={formData.hoa2Contact} onChange={e => upd('hoa2Contact', e.target.value)} /></InfoRow>
          </InfoTable>
          <SectionHead icon={<><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></>} text="Q. Metropolitan District" />
          <DiscTable>
            <DiscRow text="Property is within a Metropolitan District (organized on/after Jan 1, 2000)" val={formData.s9metro.inMetro} onChange={v => updNested('s9metro', 'inMetro', v)} />
          </DiscTable>
          {formData.s9metro.inMetro.yn === 'yes' && (
            <InfoTable>
              <InfoRow label="Metro District #1 Name"><input className="spd-info-input" type="text" placeholder="District name" value={formData.metro1Name} onChange={e => upd('metro1Name', e.target.value)} /></InfoRow>
              <InfoRow label="Official Website"><input className="spd-info-input" type="text" placeholder="Website URL" value={formData.metro1Website} onChange={e => upd('metro1Website', e.target.value)} /></InfoRow>
            </InfoTable>
          )}
        </div>
      </div>
    );
  };

  const ynLabel = (val) => val?.yn === 'yes' ? 'Yes' : val?.yn === 'no' ? 'No' : '—';

  const renderStep10 = () => (
    <div>
      <h2 className="rpa-step-heading">Review & Confirm</h2>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Review all entered information before generating the SPD19 disclosure document.</p>

      <div className="spd-review-section">
        <div className="spd-review-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg><span className="spd-review-head-text">Property</span></div>
        <div className="spd-review-row"><div className="spd-review-label">Address</div><div className="spd-review-value">{propAddress}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Year Built</div><div className="spd-review-value">{formData.yearBuilt || '—'}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Year Acquired</div><div className="spd-review-value">{formData.yearAcquired || '—'}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Currently Occupying</div><div className="spd-review-value">{formData.occupying === 'yes' ? 'Yes' : formData.occupying === 'no' ? 'No' : '—'}</div></div>
      </div>

      <div className="spd-review-section">
        <div className="spd-review-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg><span className="spd-review-head-text">Building & Roof</span></div>
        <div className="spd-review-row"><div className="spd-review-label">Structural Issues</div><div className="spd-review-value">{ynLabel(formData.s2?.structural)}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Roof Age / Material</div><div className="spd-review-value">{[formData.roofAge ? `${formData.roofAge} yrs` : null, formData.roofMaterial].filter(Boolean).join(' · ') || '—'}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Roof Warranty</div><div className="spd-review-value">{formData.roofWarranty === 'yes' ? 'Yes' : formData.roofWarranty === 'no' ? 'No' : '—'}</div></div>
      </div>

      <div className="spd-review-section">
        <div className="spd-review-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg><span className="spd-review-head-text">Water & Sewer</span></div>
        <div className="spd-review-row"><div className="spd-review-label">Water Supply Type</div><div className="spd-review-value">{formData.waterSupplyType || '—'}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Sewer Type</div><div className="spd-review-value">{formData.sewerType || '—'}</div></div>
      </div>

      <div className="spd-review-section">
        <div className="spd-review-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg><span className="spd-review-head-text">Environmental & Radon</span></div>
        <div className="spd-review-row"><div className="spd-review-label">Radon Tests Conducted</div><div className="spd-review-value">{ynLabel(formData.s7radon?.tested)}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Meth Lab History</div><div className="spd-review-value">{ynLabel(formData.s7env?.meth)}</div></div>
      </div>

      <div className="spd-review-section">
        <div className="spd-review-head"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5B47D6" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg><span className="spd-review-head-text">HOA & Metro District</span></div>
        <div className="spd-review-row"><div className="spd-review-label">Part of HOA</div><div className="spd-review-value">{ynLabel(formData.s9hoa?.isHOA)}</div></div>
        <div className="spd-review-row"><div className="spd-review-label">Metro District</div><div className="spd-review-value">{ynLabel(formData.s9metro?.inMetro)}</div></div>
      </div>

      <div className="spd-cert-box">
        <div className="spd-cert-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3d2d8f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Seller Certification
        </div>
        <div className="spd-cert-text">
          By submitting this SPD19, Seller certifies that the information contained herein is correct to Seller's
          CURRENT ACTUAL KNOWLEDGE as of the date signed. Seller acknowledges the duty to disclose any new
          adverse material facts discovered after submission.
        </div>
        <div className="spd-cert-grid">
          <div>
            <span className="spd-cert-label">Seller 1 Name</span>
            <input className="spd-info-input" type="text" placeholder="Full legal name" value={formData.sig1Name} onChange={e => upd('sig1Name', e.target.value)} style={{ borderColor: '#5B47D6' }} />
          </div>
          <div>
            <span className="spd-cert-label">Date Completed</span>
            <input className="spd-info-input" type="date" value={formData.sig1Date} onChange={e => upd('sig1Date', e.target.value)} style={{ borderColor: '#5B47D6', width: '100%' }} />
          </div>
          <div>
            <span className="spd-cert-label">Seller 2 Name (if applicable)</span>
            <input className="spd-info-input" type="text" placeholder="Full legal name" value={formData.sig2Name} onChange={e => upd('sig2Name', e.target.value)} style={{ borderColor: '#5B47D6' }} />
          </div>
          <div>
            <span className="spd-cert-label">Date Completed</span>
            <input className="spd-info-input" type="date" value={formData.sig2Date} onChange={e => upd('sig2Date', e.target.value)} style={{ borderColor: '#5B47D6', width: '100%' }} />
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:  return renderStep1();
      case 2:  return renderStep2();
      case 3:  return renderStep3();
      case 4:  return renderStep4();
      case 5:  return renderStep5();
      case 6:  return renderStep6();
      case 7:  return renderStep7();
      case 8:  return renderStep8();
      case 9:  return renderStep9();
      case 10: return renderStep10();
      default: return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="rpa-overlay" role="dialog" aria-modal="true" aria-label="SPD Wizard" onClick={handleOverlayClick}>
      <div className="rpa-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="rpa-header">
          <div className="rpa-header-left">
            <span className="rpa-header-title">SPD19 — Seller's Property Disclosure</span>
            {readOnly && <span style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED', background: '#EDE9FE', borderRadius: 4, padding: '2px 7px', marginLeft: 8, letterSpacing: '0.02em' }}>READ ONLY</span>}
            <span className="rpa-header-sub">CO · {propAddress}</span>
          </div>
          <button className="rpa-close-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Body */}
        {submitted ? (
          <div className="spd-complete">
            <div className="spd-complete-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h2>SPD19 Submitted Successfully</h2>
            <p>Your Seller's Property Disclosure has been submitted. Your listing agent has been notified and will review and prepare the SPD form for DocuSign.</p>
            <div className="spd-complete-btns">
              <button className="rpa-btn rpa-btn-primary" onClick={onClose}>Return to Transaction</button>
            </div>
          </div>
        ) : (
          <div className="rpa-body">
            {/* Sidebar stepper */}
            <nav className="rpa-sidebar" aria-label="Wizard steps">
              {STEPS.map(step => {
                const isDone   = completedSteps.has(step.id);
                const isActive = step.id === currentStep;
                const cls = `rpa-step-item ${isDone ? 'done' : isActive ? 'active' : 'todo'}`;
                return (
                  <div key={step.id} className={cls} onClick={() => isDone || isActive ? setCurrentStep(step.id) : undefined} style={{ cursor: isDone || isActive ? 'pointer' : 'default' }}>
                    <div className="rpa-step-icon">{STEP_ICONS[step.id]}</div>
                    <div className="rpa-step-meta">
                      <span className="rpa-step-num">Step {step.id}</span>
                      <span className="rpa-step-title">{step.label}</span>
                    </div>
                    {isDone && <span className="rpa-step-check">✓</span>}
                  </div>
                );
              })}
            </nav>

            {/* Content */}
            <main className="rpa-content">
              <div style={readOnly ? { pointerEvents: 'none', userSelect: 'none' } : undefined}>
                {renderCurrentStep()}
              </div>
            </main>
          </div>
        )}

        {/* Footer */}
        {!submitted && (
          <div className="rpa-footer">
            <div className="rpa-footer-left">
              {currentStep > 1 && (
                <button className="rpa-btn rpa-btn-ghost" onClick={handlePrev} disabled={isSaving}>
                  ← Back
                </button>
              )}
            </div>
            <div className="rpa-footer-center">
              <span className="rpa-footer-progress">Step {currentStep} of {TOTAL_STEPS}</span>
            </div>
            <div className="rpa-footer-right">
              {saveError && <span style={{ fontSize: 12, color: '#dc2626', marginRight: 12 }}>{saveError}</span>}
              {currentStep < TOTAL_STEPS ? (
                <button className="rpa-btn rpa-btn-primary" onClick={handleNext} disabled={!readOnly && isSaving}>
                  {!readOnly && isSaving ? 'Saving…' : readOnly ? 'Next →' : 'Save & Continue →'}
                </button>
              ) : (
                readOnly ? (
                  <button className="rpa-btn rpa-btn-primary" onClick={handleCompleteReview} disabled={isSaving}>
                    {isSaving ? 'Completing…' : 'Complete Review'}
                  </button>
                ) : (
                  <button className="rpa-btn rpa-btn-primary" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? 'Submitting…' : 'Generate SPD Summary →'}
                  </button>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SPDWizardModal;
